import { Layout } from "../../../components/Layout";
import { ReactComponent as ArrowLeft } from "../../../assets/ArrowLeft.svg";
import { ReactComponent as Logo } from "../../../assets/LogoIcon.svg";
import styles from './three.module.css';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface StepThreeProps {
    generatedCode: string;
    codeId: number | null;
    executeCode: (codeId: number) => Promise<any>;
    onBack: () => void;
}

const StepThree: React.FC<StepThreeProps> = ({
    generatedCode,
    codeId: propCodeId,
    executeCode,
    onBack
}) => {
    const params = useParams();
    const [codeId, setCodeId] = useState<number | null>(propCodeId);
    const [isExecuting, setIsExecuting] = useState<boolean>(false);
    const [executionOutput, setExecutionOutput] = useState<string>('');
    const [executionError, setExecutionError] = useState<string>('');
    const [executionStatus, setExecutionStatus] = useState<string>('');

    // Use the code ID from URL params if available
    useEffect(() => {
        const idFromUrl = params.codeId ? parseInt(params.codeId, 10) : null;
        if (idFromUrl && idFromUrl !== codeId) {
            setCodeId(idFromUrl);
        }
    }, [params.codeId, codeId]);

    const handleExecuteCode = async () => {
        if (!codeId) {
            setExecutionError('No code ID available. Please generate code first.');
            return;
        }

        setIsExecuting(true);
        setExecutionOutput('');
        setExecutionError('');
        setExecutionStatus('Running...');

        try {
            const result = await executeCode(codeId);

            if (result.outputs && result.outputs.length > 0) {
                setExecutionOutput(result.outputs.join('\n'));
            }

            if (result.errors && result.errors.length > 0) {
                setExecutionError(result.errors.join('\n'));
            }

            setExecutionStatus(result.status || 'Completed');
        } catch (error) {
            console.error('Error executing code:', error);
            setExecutionError(error instanceof Error ? error.message : String(error));
            setExecutionStatus('Failed');
        } finally {
            setIsExecuting(false);
        }
    };

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.leftPanel}>
                    <div className={styles.header}>
                        <div className={styles.backButton} role="button" onClick={onBack}>
                            <ArrowLeft />
                            <p className={styles.stepText}>Go back</p>
                        </div>
                        <p className={styles.stepText}>Step 3 of 4</p>
                    </div>
                    <div className={styles.titleContainer}>
                        <p className={styles.title}>Execute Code</p>
                        <p className={styles.subtitle}>Run your optimization code and see the results</p>
                    </div>
                    <div className={styles.codeContainer}>
                        <h3 className={styles.sectionTitle}>Generated Code</h3>
                        <SyntaxHighlighter
                            language="python"
                            style={oneDark}
                            customStyle={{ borderRadius: '8px', maxHeight: '500px' }}
                        >
                            {generatedCode || 'No code available. Please go back and generate code first.'}
                        </SyntaxHighlighter>
                        <button
                            className={styles.executeButton}
                            onClick={handleExecuteCode}
                            disabled={isExecuting || !codeId}
                        >
                            {isExecuting ? 'Executing...' : 'Execute Code'}
                        </button>
                    </div>
                </div>
                <div className={styles.rightPanel}>
                    <Logo />
                    <div className={styles.resultsContainer}>
                        <h2 className={styles.sectionTitle}>Execution Results</h2>

                        {executionStatus && (
                            <div className={styles.statusSection}>
                                <h3 className={styles.subsectionTitle}>Status</h3>
                                <p className={`${styles.status} ${executionStatus === 'Completed' ? styles.statusSuccess :
                                        executionStatus === 'Failed' ? styles.statusError :
                                            styles.statusRunning
                                    }`}>
                                    {executionStatus}
                                </p>
                            </div>
                        )}

                        {executionOutput && (
                            <div className={styles.outputSection}>
                                <h3 className={styles.subsectionTitle}>Output</h3>
                                <SyntaxHighlighter
                                    language="text"
                                    style={oneDark}
                                    customStyle={{ borderRadius: '8px' }}
                                >
                                    {executionOutput}
                                </SyntaxHighlighter>
                            </div>
                        )}

                        {executionError && (
                            <div className={styles.errorSection}>
                                <h3 className={styles.subsectionTitle}>Errors</h3>
                                <SyntaxHighlighter
                                    language="text"
                                    style={oneDark}
                                    customStyle={{ borderRadius: '8px', backgroundColor: '#FFF0F0' }}
                                >
                                    {executionError}
                                </SyntaxHighlighter>
                            </div>
                        )}

                        {!executionOutput && !executionError && !isExecuting && (
                            <div className={styles.emptyState}>
                                <p>No execution results yet. Click "Execute Code" to run your optimization.</p>
                            </div>
                        )}

                        {isExecuting && (
                            <div className={styles.loadingContainer}>
                                <div className={styles.spinner}></div>
                                <p className={styles.loadingMessage}>Executing code...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default StepThree;
